import axios from '../../axios'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { toast } from 'react-toastify'
import { Dataset, Project, Predictor, Folder } from './types'
import { foldersStore } from './folders.store'

type UserData = {
  name: string
  enterpriseLogoName?: string;
  enterpriseId: number;
  permissions: string[]
}
export class DashboardStore {
  loading: boolean = false
  user = {}
  userData: UserData | null = null
  isLoadingUserData = false
  latestProjects: Project[] = []
  latestClusteringProjects: Project[] = []
  latestMpoProjects: Project[] = []
  latestDatasets: Dataset[] = []
  latestPredictors: Predictor[] = []
  latestFolders: Folder[] = []
  projectsCount: number = 0
  clusteringProjectsCount: number = 0
  mpoProjectsCount: number = 0
  datasetsCount: number = 0
  predictorsCount: number = 0
  foldersCount: number = 0
  currentAuthenticatedUser = null

  constructor() {
    makeObservable(this, {
      loading: observable,
      user: observable,
      userData: observable,
      latestProjects: observable,
      projectsCount: observable,
      latestClusteringProjects: observable,
      clusteringProjectsCount: observable,
      latestDatasets: observable,
      datasetsCount: observable,
      latestPredictors: observable,
      predictorsCount: observable,
      latestFolders: observable,
      foldersCount: observable,
      latestMpoProjects: observable,
      mpoProjectsCount: observable,
      getData: action,
      setUserData: action,
      setIsLoadingUserData: action,
      setIsLoading: action,
      reset: action,
      permissions: computed,
      isAdminUser: computed,
    })
  }
  setIsLoading = (value: boolean) => {
    this.loading = value
  }
  setIsLoadingUserData = (value: any) => {
    this.isLoadingUserData = value
  }
  setUserData = (value: any) => {
    this.userData = value
  }

  get permissions(): { [key: string]: boolean } {
    const result: { [key: string]: boolean } = {}
    if (this.userData?.permissions?.length) {
      this.userData.permissions.forEach((permission) => {
        result[permission] = true
      })
    }
    return result
  }

  get isAdminUser(): boolean {
    if (!this.userData?.permissions) {
      return false
    }
    const permissions = this.userData.permissions
    return permissions.includes('ENTERPRISE_ADMIN') || permissions.includes('SYSTEM_ADMIN')
  }

  getUserData = async () => {
    this.setIsLoadingUserData(true)
    const data = await axios('/api/v1/user/current')
      .then(({ data }) => data)
      .finally(() => {
        this.setIsLoadingUserData(false)
      })
    this.setUserData(data)
    return data
  }

  reset = () => {
    this.loading = false
    this.user = {}
    this.userData = null
    this.isLoadingUserData = true
    this.latestProjects = []
    this.latestClusteringProjects = []
    this.latestDatasets = []
    this.latestPredictors = []
    this.projectsCount = 0
    this.clusteringProjectsCount = 0
    this.datasetsCount = 0
    this.predictorsCount = 0
    this.currentAuthenticatedUser = null
  }
  getInformation = async () => {
    const response = await axios('/api/v1/dashboard/information')
    return response.data;
  }
  getData = async () => {
    this.setIsLoading(true)
    try {
      const [information, foldersResponse] = await Promise.all([this.getInformation(), foldersStore.getLatestFolders()])
      if (!information) return
      runInAction(() => {
        this.user = information.user
        this.latestProjects = information.projects
        this.latestClusteringProjects = information.clusteringProjects
        this.latestMpoProjects = information.mpoProjects
        this.latestDatasets = information.datasets
        this.latestPredictors = information.predictors
        this.datasetsCount = information.datasetsCount
        this.projectsCount = information.projectsCount
        this.clusteringProjectsCount = information.clusteringProjectsCount
        this.mpoProjectsCount = information.mpoProjectsCount
        this.predictorsCount = information.predictorsCount
        this.latestFolders = foldersResponse?.content || []
        this.foldersCount = foldersResponse?.totalElements || 0
      })
    } catch (error) {
      console.error(error)
    } finally {
      this.setIsLoading(false)
      this.loading = false
    }
  }

  reportAnIssue = async (body: any) => {
    return await axios
      .post('/management/api/v1/issues', body)
      .then((response) => {
        toast.success('Your issue has been sent to Artemis AI team')
        return response
      })
  }

  submitContactUs = async (body: FormData) => {
    return await axios
      .post('/management/api/v1/issues/submit-contactus', body)
      .then((response) => {
        toast.success('We received your inquiry, one of our team members will contact you soon')
        return response
      })
  }
}

export const dashboardStore = new DashboardStore()
